/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BlogCardFragment } from "@cosmos/hygraph-schema";
import { Avatar } from "@eds/react/Avatar";
import { useLocale } from "@epic-mw/i18n";

import {
	Animate,
	ButtonList,
	CosmosAsset,
	CosmosCard,
	CosmosDate,
	CosmosDesc,
	CosmosTitle,
	CosmosTagProps,
	CosmosText,
	CosmosTextVariantEnum,
	EdsIcon,
	Wrap,
} from "@components/elements";
import type { CosmosCardProps } from "@components/elements";
import { css, cx } from "@panda/css";
import { HStack, styled, VStack } from "@panda/jsx";
import { useCosmosComponentContext } from "@components/context";
import { getFeedCardHref } from "@components/elements/molecules/feed-cards/feed-card.utils";

export type BlogCardVariant = "single" | "double" | "triple" | "quadruple" | undefined;

export interface BlogCardProps extends BlogCardFragment {
	direction?: CosmosCardProps["direction"];
	className?: string;
	justifyContent?: "center" | "flex-start";
	badges?: CosmosTagProps[];
	variant?: BlogCardVariant;
	enableAnimate?: boolean;
	animateDelay?: number;
	date?: string | null;
	likeCount?: number;
	replyCount?: number;
	author?: {
		name: string;
		staff?: boolean;
		avatar?: string;
	};
	originUrl?: string;
	footer?: string | null;
	hideCategoryPills?: boolean;
	sizes?: string;
}

const cardStyles = css({
	"&&": {
		gap: "eds.dimension.16",
		"&.row .blog-card-content, &.row .cosmos-card__thumbnail": {
			flexBasis: "50%",
		},
		"&.mdRow .blog-card-content, &.mdRow .cosmos-card__thumbnail": {
			md: {
				flexBasis: "50%",
			},
		},
	},
});

const socialStyles = css({
	width: "100%",
	justifyContent: "space-between",
	"& .eds-avatar": {
		gap: "2px",
		"& > span:first-child": {
			outlineWidth: "1.22px",
			outlineStyle: "solid",
			outlineOffset: "2.44px",
			borderRadius: "eds.dimension.16",
		},
	},
	"& .cosmos-text": {
		color: "#FCFDFD",
	},
	smDown: {
		flexDirection: "column",
	},
});

const blogCardDateStyles = css({ textStyle: "uiSM", color: "eds.text.secondary" });
const blogCardEyebrpwStyles = css({ color: "eds.text.secondary" });
const blogCardTitleStyles = css({ fontWeight: "bold" });
const blogCardFooterStyles = css({ marginBlockStart: "eds.dimension.8" });

export function BlogCard({
	className,
	asset,
	href,
	badges = [],
	category,
	title,
	desc,
	direction = "column",
	justifyContent = "flex-start",
	variant,
	enableAnimate,
	animateDelay,
	date,
	likeCount,
	replyCount,
	author,
	originUrl,
	buttons,
	footer,
	eyebrow,
	hideCategoryPills,
	sizes,
}: BlogCardProps): React.JSX.Element {
	const locale = useLocale();
	const { feedRootPath = "/feed", locales, feedBrand } = useCosmosComponentContext();
	const tags: CosmosTagProps[] = [
		...badges.filter((b): b is CosmosTagProps & { label: string } => Boolean(b.label)),
		...(!hideCategoryPills && category?.tag?.tagId
			? [
					{
						label: category.label,
						href: `/${locale}${feedRootPath}?tags=${category.tag.tagId}`,
					},
				]
			: []),
	];

	return (
		<Wrap
			condition={enableAnimate || false}
			wrap={(children) => (
				<Animate delay={animateDelay || 0.5} type="slide" customCss={css.raw({ w: "full" })}>
					{children}
				</Animate>
			)}
		>
			<CosmosCard
				padding={16}
				className={cx("blog-card", cardStyles, className, direction, variant)}
				variant="material"
				direction={direction}
			>
				<CosmosCard.Thumbnail aspectRatio="16/9">
					<CosmosAsset asset={asset} tags={tags} sizes={sizes} />
				</CosmosCard.Thumbnail>
				<VStack
					className={cx("blog-card-content")}
					justifyContent="space-between"
					flexGrow="1"
					minWidth="0"
					alignItems="flex-start"
					gap="eds.dimension.16"
				>
					<VStack justifyContent={justifyContent} alignItems="flex-start" gap="eds.dimension.8">
						{date ? <CosmosDate date={date} className={blogCardDateStyles} /> : null}
						{eyebrow ? (
							<div className="eyebrow">
								<CosmosText
									className={cx("blog-card-eyebrow", blogCardEyebrpwStyles)}
									text={eyebrow}
									variant={CosmosTextVariantEnum.eyebrowSM}
								/>
								<styled.div height="eds.dimension.8" />
							</div>
						) : null}
						<CosmosCard.Link
							href={getFeedCardHref(locale, locales, { url: href || "", originUrl }, feedBrand)}
							analytics={{
								eventCategory: "tile",
								eventLabel: title?.text || "",
								eventValue: {
									component: "BlogCard",
									href,
								},
							}}
						>
							<CosmosTitle
								className={cx("blog-card-title", blogCardTitleStyles)}
								title={{ html: title?.html, text: title?.text, variant: CosmosTextVariantEnum.uiLG }}
							/>
						</CosmosCard.Link>
						<CosmosDesc
							className="blog-card-desc"
							description={{
								html: desc,
								variant: CosmosTextVariantEnum.paragraphSM,
								color: "eds.text.secondary",
							}}
						/>
						{footer?.length ? (
							<CosmosDesc
								className={cx("blog-card-footer", blogCardFooterStyles)}
								description={{
									html: footer,
									variant: CosmosTextVariantEnum.uiSM,
									color: "eds.text.primary",
								}}
							/>
						) : null}
					</VStack>
					{author?.name || likeCount || replyCount ? (
						<HStack
							className={cx("social-info", socialStyles)}
							gap="eds.dimension.16"
							flexWrap="wrap"
							alignItems="flex-start"
						>
							{author ? (
								<Avatar
									className={cx(
										"eds-avatar",
										css({
											"& > span:first-child": {
												outlineColor: author.staff ? "eds.fill.primary.default" : "transparent",
											},
										}),
									)}
									image={author?.avatar}
									label={author.name || ""}
									aria-label={author.name || ""}
									icon
									size="sm"
								/>
							) : null}
							<HStack gap="eds.dimension.16">
								{likeCount ? (
									<HStack gap="eds.dimension.6">
										<EdsIcon size="sm" icon="ThumbUp" color="secondary" />
										<CosmosText variant="uiSM">{likeCount}</CosmosText>
									</HStack>
								) : null}
								{replyCount ? (
									<HStack gap="eds.dimension.6">
										<EdsIcon size="sm" icon="TalkBubbleDots" color="secondary" />
										<CosmosText variant="uiSM">{replyCount}</CosmosText>
									</HStack>
								) : null}
							</HStack>
						</HStack>
					) : null}
					{Array.isArray(buttons) && buttons?.length ? <ButtonList buttons={buttons} iconSize="sm" /> : null}
				</VStack>
			</CosmosCard>
		</Wrap>
	);
}
